
import React, { useState, useEffect } from "react";
import { useOnboardingState } from "@/integrations/supabase/onboarding";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import BusinessOwnerTwilioSetup from "./BusinessOwnerTwilioSetup";
import AgencyTwilioCredentialsForm from "./AgencyTwilioCredentialsForm";

const Step1Twilio = ({ onNext }: { onNext: () => void }) => {
  const { onboarding, updateTwilio, validateTwilioCredentials } = useOnboardingState();
  const { user } = useAuth();
  const userRole = user?.app_metadata?.role || "business_owner";

  const [accountSid, setAccountSid] = useState("");
  const [authToken, setAuthToken] = useState("");
  const [revealToken, setRevealToken] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [validated, setValidated] = useState(false);

  const [creatingSub, setCreatingSub] = useState(false);
  const [subResult, setSubResult] = useState<string | null>(null);

  useEffect(() => {
    if (onboarding?.twilio_sid) setAccountSid(onboarding.twilio_sid);
  }, [onboarding]);

  const hasMaskedDbToken = !!onboarding?.twilio_auth_token;

  const handleValidate = async () => {
    setLoading(true);
    setError(null);
    setValidated(false);
    try {
      let tokenForValidation = authToken;
      if (!tokenForValidation && hasMaskedDbToken) {
        setError("Please enter your Twilio Auth Token to proceed.");
        setLoading(false);
        setValidated(false);
        return;
      }
      if (!accountSid || !tokenForValidation) {
        setError("Please enter both Twilio Account SID and Auth Token.");
        setLoading(false);
        return;
      }

      const valid = await validateTwilioCredentials(accountSid, tokenForValidation);
      if (!valid) {
        setError("Invalid Twilio credentials. Please check and try again.");
        setLoading(false);
        setValidated(false);
        return;
      }
      toast.success("Twilio Credentials Validated Successfully!", {
        description: "Your numbers will be imported shortly.",
      });
      setValidated(true);

      await updateTwilio(accountSid, tokenForValidation, { isAgency: true });
      setLoading(false);
      setError(null);
      onNext();
    } catch (err: any) {
      setLoading(false);
      setValidated(false);
      setError(err.message || "Failed to validate credentials.");
      toast.error("Twilio validation failed", { description: err.message || "Please try again." });
    }
  };

  const handleSubaccount = async () => {
    setCreatingSub(true);
    setError(null);
    setSubResult(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        throw new Error("Authentication required. Please refresh and try again.");
      }
      if (!onboarding?.tenant_id) {
        throw new Error("Tenant ID not found. Please contact support.");
      }
      
      const res = await fetch("https://ktywphxmqafinzfgzhps.functions.supabase.co/Calie_create_subaccount", {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${sessionData.session.access_token}`
        },
        body: JSON.stringify({ tenant_id: onboarding.tenant_id }),
      });
      
      const responseData = await res.json();

      if (res.status === 207 && responseData.partialSuccess) {
        setSubResult("Twilio sub-account created, but with limited functionality!");
        toast.warning("Twilio sub-account creation incomplete", {
          description: responseData.error || "Admin needs to configure encryption. Some features may be limited."
        });
        // Don't auto-proceed - let user decide after seeing the warning
        return;
      }

      if (!res.ok) {
        throw new Error(responseData.error || "Failed to create Twilio sub-account.");
      }

      setSubResult("Twilio sub-account created successfully!");
      toast.success("Twilio sub-account created!", {
        description: "Now you can set up your first tracking number."
      });
      setTimeout(() => onNext(), 1000);
    } catch (err: any) {
      console.error("Sub-account creation error:", err);
      setError(err.message || "Something went wrong creating your Twilio account.");
      toast.error("Failed to create Twilio account", {
        description: err.message || "Please try again or contact support."
      });
    } finally {
      setCreatingSub(false);
    }
  };

  if (userRole === "business_owner") {
    return (
      <BusinessOwnerTwilioSetup
        error={error}
        subResult={subResult}
        creatingSub={creatingSub}
        handleSubaccount={handleSubaccount}
        onNext={onNext}
      />
    );
  }

  return (
    <AgencyTwilioCredentialsForm
      error={error}
      validated={validated}
      accountSid={accountSid}
      setAccountSid={setAccountSid}
      authToken={authToken}
      setAuthToken={setAuthToken}
      hasMaskedDbToken={hasMaskedDbToken}
      revealToken={revealToken}
      setRevealToken={setRevealToken}
      loading={loading}
      handleValidate={handleValidate}
    />
  );
};

export default Step1Twilio;
